import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, 
  Box, 
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider, 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography 
} from '@mui/material';
import { Celebration, Delete, LocalOffer, Notifications, MailOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useNotificationsWebSocket } from '../../../hooks/useNotificationsWebSocket';
import { useAuth } from '../../../context/AuthContext';
import { axiosClient } from '../../../services/axiosClient';
import {
  getAppointmentNotificationPath,
  isAppointmentNotification,
} from '../../../utils/appointmentNotificationNavigation';
import AppointmentNotificationDetails from './AppointmentNotificationDetails';
import ReviewModal from '../reviews/ReviewModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';

const NotificationsDropdown = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ open: false, appointmentId: null, salonId: null, salonName: null });
  const [messageDialog, setMessageDialog] = useState({ open: false, title: '', message: '', salonId: null, salonName: '', type: '' });
  const [closureDialog, setClosureDialog] = useState({ open: false, notification: null });

  const isClientUser = user?.role === 'client';
  const isProfessionalUser = typeof user?.role === 'string' && user.role.startsWith('professional');

  const wsToken = useMemo(() => {
    return user?.token || localStorage.getItem('access_token') || '';
  }, [user]);

  const wsEnabled = Boolean(wsToken) && isAuthenticated();

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated()) return;
    setIsLoading(true);
    try {
      if (isClientUser) {
        await axiosClient.get('/salons/reviews/pending/');
      }
      const res = await axiosClient.get('/notifications/');
      const items = Array.isArray(res?.data) ? res.data : [];
      setNotifications(
        items.map((n) => ({
          id: n.id,
          title: n.title,
          message: n.message,
          date: n.created_at,
          read: Boolean(n.read_at),
          type: n.type,
          data: n.data,
        }))
      );
    } catch (_e) {
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isClientUser]);

  useEffect(() => {
    if (wsEnabled) {
      loadNotifications();
    } else {
      setNotifications([]);
    }
  }, [loadNotifications, wsEnabled]);

  const handleWsMessage = useCallback((payload) => {
    if (!payload) return;

    if (payload.event === 'notification.created' && payload.notification) {
      const n = payload.notification;
      setNotifications((prev) => {
        const exists = prev.some((x) => x.id === n.id);
        if (exists) return prev;

        return [
          {
            id: n.id,
            title: n.title,
            message: n.message,
            date: n.created_at,
            read: Boolean(n.read_at),
            type: n.type,
            data: n.data,
          },
          ...prev,
        ];
      });
    }
  }, []);

  useNotificationsWebSocket({ enabled: wsEnabled, token: wsToken, onMessage: handleWsMessage });

  const unreadCount = notifications.filter((n) => !n.read).length;
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkRead = async (id) => {
    try {
      await axiosClient.post(`/notifications/${id}/mark_read/`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (_e) {
      // ignore
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;

    if (!notification.read) {
      await handleMarkRead(notification.id);
    }

    const type = notification.type;
    const data = notification.data || {};
    const appointmentPath = getAppointmentNotificationPath(notification, {
      isProfessionalUser,
      isClientUser,
    });

    if (type === 'admin_closure_request') {
      setClosureDialog({ open: true, notification });
      handleClose();
      return;
    }

    if (appointmentPath) {
      navigate(appointmentPath);
      handleClose();
      return;
    }

    if (type === 'review_pending' && data.salon_id && data.appointment_id) {
      // Ouvrir le dialog de review au lieu de naviguer
      setReviewDialog({
        open: true,
        appointmentId: data.appointment_id,
        salonId: data.salon_id,
        salonName: data.salon_name || null
      });
      handleClose();
      return;
    }

    if (type === 'salon_message' && data.salon_id) {
      setMessageDialog({
        open: true,
        title: notification.title || 'Notification',
        message: notification.message || '',
        salonId: data.salon_id || null,
        salonName: data.salon_name || '',
        type,
      });
      handleClose();
      return;
    }

    if (type === 'promotion_alert' && data.salon_id) {
      setMessageDialog({
        open: true,
        title: notification.title || 'Promotion',
        message: notification.message || '',
        salonId: data.salon_id || null,
        salonName: data.salon_name || '',
        type,
      });
      handleClose();
      return;
    }

    if (type === 'review_created' && isProfessionalUser && data.salon_id) {
      navigate(`/pro/salon/${data.salon_id}?openReviews=true`);
      handleClose();
      return;
    }

    // Default behavior: show notification details
    setMessageDialog({
      open: true,
      title: notification.title || 'Notification',
      message: notification.message || '',
      salonId: data.salon_id || null,
      salonName: data.salon_name || '',
      type,
    });
    handleClose();
  };

  const submitClosureResponse = async (accept) => {
    const n = closureDialog.notification;
    if (!n?.id) {
      setClosureDialog({ open: false, notification: null });
      return;
    }

    try {
      await axiosClient.post(`/notifications/${n.id}/respond-closure/`, { accept: !!accept });
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    } catch (_e) {
      // ignore
    } finally {
      setClosureDialog({ open: false, notification: null });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosClient.post(`/notifications/${id}/mark_deleted/`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (_e) {
      // ignore
    }
  };

  const handleClear = async () => {
    try {
      await axiosClient.post('/notifications/clear/');
      setNotifications([]);
      handleClose();
    } catch (_e) {
      // ignore
    }
  };

  const toSafeHtml = (value) => {
    const raw = value == null ? '' : String(value);
    const looksLikeHtml = /<\s*\w+[^>]*>/i.test(raw);
    if (looksLikeHtml) return raw;

    const escaped = raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r\n|\r|\n/g, '<br/>');
    return `<p>${escaped}</p>`;
  };

  const toPlainText = (value) => {
    const raw = value == null ? '' : String(value);
    try {
      if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
        const doc = new window.DOMParser().parseFromString(raw, 'text/html');
        return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
      }
    } catch (_e) {
      // ignore
    }

    return raw
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return t('notifications.today');
    } else if (diffDays === 1) {
      return t('notifications.yesterday');
    } else if (diffDays < 7) {
      return t('notifications.days_ago', { count: diffDays });
    } else {
      return date.toLocaleDateString(t('locale'));
    }
  };

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="notifications"
        aria-controls="menu-notifications"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        id="menu-notifications"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: '90vw',
            maxHeight: '80vh',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('notifications.title')}
          </Typography>
        </Box>
        <Divider />
        
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.loading')}
            </Typography>
          </Box>
        ) : notifications.length > 0 ? (
          <div>
            {notifications.map((notification) => {
              const isAppointmentForCurrentUser =
                (isProfessionalUser || isClientUser) &&
                isAppointmentNotification(notification);
              const locale =
                i18n.resolvedLanguage || i18n.language || t('locale') || 'fr-FR';

              return (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{ 
                  py: 1.5,
                  px: 2,
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': { backgroundColor: 'action.selected' },
                  alignItems: 'flex-start',
                  minHeight: 'auto',
                }}
              >
                <Box sx={{ 
                  width: '100%', 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'flex-start' 
                }}>
                  {/* Contenu principal */}
                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    {/* Titre */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: notification.read ? 'regular' : 'bold',
                        wordBreak: 'break-word',
                        mb: 0.5,
                      }}
                    >
                      {notification.title}
                    </Typography>
                    
                    {isAppointmentForCurrentUser ? (
                      <AppointmentNotificationDetails
                        data={notification.data}
                        locale={locale}
                        audience={isClientUser ? 'client' : 'professional'}
                        fallbackDate={formatDate(notification.date)}
                        fallbackMessage={toPlainText(notification.message)}
                      />
                    ) : (
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          {formatDate(notification.date)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            mb: !notification.read ? 1 : 0,
                          }}
                        >
                          {toPlainText(notification.message)}
                        </Typography>
                      </>
                    )}
                    
                    {/* Bouton marquer comme lu */}
                    {!notification.read && (
                      <Box sx={{ mt: 1 }}>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            handleMarkRead(notification.id); 
                          }}
                        >
                          {t('notifications.mark_as_read').toUpperCase()}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Bouton supprimer - toujours à droite */}
                  <IconButton
                    size="small"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      handleDelete(notification.id); 
                    }}
                    sx={{ 
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </MenuItem>
              );
            })}
            <Divider />
            <MenuItem 
              onClick={handleClear}
              sx={{ justifyContent: 'center', color: 'error.main' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {t('notifications.clear')}
              </Typography>
            </MenuItem>
          </div>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.no_notifications')}
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Dialog de review */}
      <ReviewModal
        open={reviewDialog.open}
        onClose={() => setReviewDialog({ open: false, appointmentId: null, salonId: null, salonName: null })}
        appointment={{ appointment_id: reviewDialog.appointmentId, salon: reviewDialog.salonId }}
        salonId={reviewDialog.salonId}
        salonName={reviewDialog.salonName}
        onSuccess={() => {
          loadNotifications();
          setReviewDialog({ open: false, appointmentId: null, salonId: null, salonName: null });
        }}
      />

      <Dialog
        open={messageDialog.open}
        onClose={() => setMessageDialog({ open: false, title: '', message: '', salonId: null, salonName: '', type: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          }
        }}
      >
        {messageDialog.type === 'promotion_alert' ? (
          <DialogTitle
            sx={{
              p: 0,
              background: 'linear-gradient(135deg, #ff6a00 0%, #ee0979 55%, #7f00ff 100%)',
              color: 'white',
            }}
          >
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  flexShrink: 0,
                }}
              >
                <Celebration />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: '1.1rem' }}>
                  {messageDialog.title}
                </Typography>
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {(messageDialog.salonId || messageDialog.salonName) ? (
                    <Chip
                      icon={<LocalOffer sx={{ color: 'white !important' }} />}
                      label={messageDialog.salonName ? `Salon: ${messageDialog.salonName}` : `Salon #${messageDialog.salonId}`}
                      size="small"
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        fontWeight: 700,
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </DialogTitle>
        ) : messageDialog.type === 'salon_message' ? (
          <DialogTitle
            sx={{
              p: 0,
              background: 'linear-gradient(135deg, #2e7d32 0%, #2e7d32 30%, #0f766e 100%)',
              color: 'white',
            }}
          >
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  flexShrink: 0,
                }}
              >
                <MailOutline />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: '1.1rem' }}>
                  {messageDialog.title}
                </Typography>
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {(messageDialog.salonId || messageDialog.salonName) ? (
                    <Chip
                      icon={<LocalOffer sx={{ color: 'white !important' }} />}
                      label={messageDialog.salonName ? `Salon: ${messageDialog.salonName}` : `Salon #${messageDialog.salonId}`}
                      size="small"
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.25)',
                        fontWeight: 700,
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </DialogTitle>
        ) : (
          <DialogTitle sx={{ fontWeight: 800 }}>{messageDialog.title}</DialogTitle>
        )}
        
        <DialogContent
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            pt: messageDialog.type === 'promotion_alert' ? 2.5 : undefined,
          }}
        >
          {(messageDialog.type !== 'promotion_alert' && messageDialog.type !== 'salon_message') && (messageDialog.salonId || messageDialog.salonName) && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Salon :</strong> {messageDialog.salonName || messageDialog.salonId}
            </Typography>
          )}

          {messageDialog.type === 'promotion_alert' ? (
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                backgroundColor: 'rgba(0,0,0,0.02)',
                p: 2,
              }}
            >
              <Typography sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                Détails de la promotion
              </Typography>
              <ReactQuill
                theme="bubble"
                value={toSafeHtml(messageDialog.message)}
                readOnly
                modules={{ toolbar: false }}
              />
            </Box>
          ) : messageDialog.type === 'salon_message' ? (
            <Box
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                backgroundColor: 'rgba(46, 125, 50, 0.06)',
                p: 2,
              }}
            >
              <Typography sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                Message du salon
              </Typography>
              <ReactQuill
                theme="bubble"
                value={toSafeHtml(messageDialog.message)}
                readOnly
                modules={{ toolbar: false }}
              />
            </Box>
          ) : (
            <ReactQuill
              theme="bubble"
              value={toSafeHtml(messageDialog.message)}
              readOnly
              modules={{ toolbar: false }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {messageDialog.salonId && (
            <Button
              onClick={() => {
                const salonPath = isProfessionalUser
                  ? `/pro/salon/${messageDialog.salonId}`
                  : `/salon/${messageDialog.salonId}`;
                navigate(salonPath);
                setMessageDialog({ open: false, title: '', message: '', salonId: null, salonName: '', type: '' });
              }}
              variant={messageDialog.type === 'promotion_alert' ? 'contained' : undefined}
              sx={
                messageDialog.type === 'promotion_alert'
                  ? { borderRadius: 999, px: 2.5, fontWeight: 900 }
                  : undefined
              }
            >
              Aller voir le salon
            </Button>
          )}
          <Button onClick={() => setMessageDialog({ open: false, title: '', message: '', salonId: null, salonName: '', type: '' })}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closureDialog.open}
        onClose={() => setClosureDialog({ open: false, notification: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{closureDialog.notification?.title || 'Fermeture exceptionnelle'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {closureDialog.notification?.message || "Confirmez-vous cette fermeture ?"}
          </Typography>
          {(() => {
            const d = closureDialog.notification?.data || {};
            const start = d.start_datetime ? new Date(d.start_datetime) : null;
            const end = d.end_datetime ? new Date(d.end_datetime) : null;
            const reason = d.reason;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {start && end && (
                  <Typography variant="caption" color="text.secondary">
                    Du {start.toLocaleString()} au {end.toLocaleString()}
                  </Typography>
                )}
                {reason && (
                  <Typography variant="caption" color="text.secondary">
                    Motif: {reason}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => submitClosureResponse(false)} variant="outlined">
            Non
          </Button>
          <Button onClick={() => submitClosureResponse(true)} variant="contained">
            Oui
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsDropdown;
